import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { apiRequest } from "@/lib/queryClient";
import type { ResellerConfig, AiPricingTier, SubscriptionPlan, Coupon, PlanFeatures, Affiliate, AffiliateReferral, AffiliatePayout } from "@shared/schema";
import { insertSubscriptionPlanSchema, FEATURE_REGISTRY } from "@shared/schema";
import { Store, Palette, Globe, CreditCard, Settings, Eye, Bot, Plus, Edit, Trash2, Star, DollarSign, Check, X, Move, Ticket, Calendar, Users, RefreshCw, Link2, Code2, Copy, CheckCheck, ExternalLink, Calculator, Cpu, HardDrive, Film, TrendingUp, Info } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { formatDateInput, getTodayDateString } from "@/lib/dateUtils";
import { calcAiCommit, calcMediaCommit, calcStorageCommit } from "@/lib/resellerCalcUtils";

const resellerConfigSchema = z.object({
  companyName: z.string().min(1, "Company name is required"),
  companyLogo: z.string().url().optional().or(z.literal("")),
  heroTitle: z.string().min(1, "Hero title is required"),
  heroSubtitle: z.string().min(1, "Hero subtitle is required"),
  primaryColor: z.string().regex(/^#[0-9A-F]{6}$/i, "Must be a valid hex color"),
  secondaryColor: z.string().regex(/^#[0-9A-F]{6}$/i, "Must be a valid hex color"),
  showPricing: z.boolean(),
  customPricingMessage: z.string().optional(),
  supportEmail: z.string().email().optional().or(z.literal("")),
  website: z.string().url().optional().or(z.literal("")),
  termsUrl: z.string().url().optional().or(z.literal("")),
  privacyUrl: z.string().url().optional().or(z.literal("")),
  stripeAccountId: z.string().optional().or(z.literal("")),
  enableCustomDomains: z.boolean(),
  enableWhiteLabeling: z.boolean(),
});

type ResellerConfigForm = z.infer<typeof resellerConfigSchema>;

// Coupons Management Component
function CouponsManagement() {
  const queryClient = useQueryClient();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [deletingCouponId, setDeletingCouponId] = useState<string | null>(null);

  const { data: coupons, isLoading } = useQuery<Coupon[]>({
    queryKey: ["/api/reseller/coupons"],
  });

  const createCouponMutation = useMutation({
    mutationFn: async (data: any) => {
      return await apiRequest("POST", "/api/reseller/coupons", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reseller/coupons"] });
      setIsCreateDialogOpen(false);
    },
    onError: (error: any) => {
    },
  });

  const updateCouponMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return await apiRequest("PATCH", `/api/reseller/coupons/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reseller/coupons"] });
      setEditingCoupon(null);
    },
    onError: (error: any) => {
    },
  });

  const deleteCouponMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest("DELETE", `/api/reseller/coupons/${id}`, null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reseller/coupons"] });
      setDeletingCouponId(null);
    },
    onError: (error: any) => {
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Ticket className="w-5 h-5" />
          Coupon Management
        </CardTitle>
        <CardDescription>
          Create and manage discount coupons for your subscription plans
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex justify-end mb-4">
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2" data-testid="button-create-coupon">
                <Plus className="w-4 h-4" />
                Create Coupon
              </Button>
            </DialogTrigger>
            <DialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
              <CouponForm 
                onSubmit={(data) => createCouponMutation.mutate(data)}
                onCancel={() => setIsCreateDialogOpen(false)}
                isSubmitting={createCouponMutation.isPending}
              />
            </DialogContent>
          </Dialog>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Redemptions</TableHead>
                  <TableHead>Valid Period</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coupons?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No coupons created. Click "Create Coupon" to get started.
                    </TableCell>
                  </TableRow>
                )}
                {coupons?.map((coupon) => (
                  <TableRow key={coupon.id} data-testid={`row-coupon-${coupon.id}`}>
                  <TableCell>
                    <div className="font-mono font-medium">{coupon.code}</div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{coupon.name}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {coupon.discountType === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">
                      {coupon.discountType === 'percentage' 
                        ? `${coupon.discountValue}%` 
                        : `$${(coupon.discountValue / 100).toFixed(2)}`}
                    </span>
                    {coupon.freeTrialDays && coupon.freeTrialDays > 0 && (
                      <div className="text-sm text-muted-foreground">
                        + {coupon.freeTrialDays} day trial
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {coupon.timesRedeemed || 0}
                      {coupon.maxRedemptions ? ` / ${coupon.maxRedemptions}` : ' / ∞'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div>{coupon.validFrom ? new Date(coupon.validFrom).toLocaleDateString() : 'Now'}</div>
                      <div className="text-muted-foreground">
                        to {coupon.validUntil ? new Date(coupon.validUntil).toLocaleDateString() : 'Never'}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={coupon.isActive ? "default" : "secondary"} data-testid={`badge-status-${coupon.id}`}>
                      {coupon.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Dialog open={editingCoupon?.id === coupon.id} onOpenChange={(open) => !open && setEditingCoupon(null)}>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingCoupon(coupon)}
                            data-testid={`button-edit-coupon-${coupon.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
                          <CouponForm 
                            coupon={coupon}
                            onSubmit={(data) => updateCouponMutation.mutate({ id: coupon.id, data })}
                            isSubmitting={updateCouponMutation.isPending}
                          />
                        </DialogContent>
                      </Dialog>
                      <Dialog open={deletingCouponId === coupon.id} onOpenChange={(open) => !open && setDeletingCouponId(null)}>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeletingCouponId(coupon.id)}
                            data-testid={`button-delete-coupon-${coupon.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Delete Coupon</DialogTitle>
                            <DialogDescription>
                              Are you sure you want to delete the coupon "{coupon.code}"? This action cannot be undone.
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setDeletingCouponId(null)} data-testid="button-cancel-delete">
                              Cancel
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={() => deleteCouponMutation.mutate(coupon.id)}
                              disabled={deleteCouponMutation.isPending}
                              data-testid="button-confirm-delete"
                            >
                              {deleteCouponMutation.isPending ? "Deleting..." : "Delete"}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Coupon Form Component
function CouponForm({ coupon, onSubmit, onCancel, isSubmitting }: { coupon?: Coupon; onSubmit: (data: any) => void; onCancel?: () => void; isSubmitting: boolean }) {
  const [formData, setFormData] = useState({
    code: coupon?.code || '',
    name: coupon?.name || '',
    discountType: coupon?.discountType || 'percentage',
    discountValue: coupon?.discountValue || 0,
    freeTrialDays: coupon?.freeTrialDays || 0,
    applyToMonthly: coupon?.applyToMonthly ?? true,
    applyToAnnual: coupon?.applyToAnnual ?? true,
    monthlyDiscountOverride: coupon?.monthlyDiscountOverride || '',
    annualDiscountOverride: coupon?.annualDiscountOverride || '',
    maxRedemptions: coupon?.maxRedemptions || '',
    validFrom: coupon?.validFrom ? formatDateInput(new Date(coupon.validFrom)) : getTodayDateString(),
    validUntil: coupon?.validUntil ? formatDateInput(new Date(coupon.validUntil)) : '',
    isActive: coupon?.isActive ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate code format
    const code = formData.code.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (code.length < 3) {
      alert('Coupon code must be at least 3 characters long (alphanumeric only)');
      return;
    }

    // Prepare data for submission
    const submitData: any = {
      ...formData,
      code,
      maxRedemptions: formData.maxRedemptions ? parseInt(formData.maxRedemptions as string) : null,
      monthlyDiscountOverride: formData.monthlyDiscountOverride ? parseInt(formData.monthlyDiscountOverride as string) : null,
      annualDiscountOverride: formData.annualDiscountOverride ? parseInt(formData.annualDiscountOverride as string) : null,
      validFrom: new Date(formData.validFrom),
      validUntil: formData.validUntil ? new Date(formData.validUntil) : null,
    };

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <DialogHeader>
        <DialogTitle>{coupon ? 'Edit Coupon' : 'Create New Coupon'}</DialogTitle>
        <DialogDescription>
          {coupon ? 'Update the coupon details below.' : 'Fill in the details to create a new discount coupon.'}
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="code">Coupon Code *</Label>
          <Input
            id="code"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
            placeholder="SUMMER2024"
            required
            data-testid="input-coupon-code"
          />
          <p className="text-xs text-muted-foreground">Alphanumeric only, will be converted to uppercase</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Display Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Summer Sale 2024"
            required
            data-testid="input-coupon-name"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="discountType">Discount Type *</Label>
          <Select 
            value={formData.discountType} 
            onValueChange={(value) => setFormData({ ...formData, discountType: value })}
          >
            <SelectTrigger id="discountType" data-testid="select-discount-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="percentage">Percentage</SelectItem>
              <SelectItem value="fixed_amount">Fixed Amount</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="discountValue">Discount Value *</Label>
          <NumberField
            id="discountValue"
            min={0}
            max={formData.discountType === 'percentage' ? 100 : undefined}
            allowDecimal={formData.discountType === 'percentage'}
            emptyValue={0}
            value={formData.discountValue}
            onValueChange={(v) => setFormData({ ...formData, discountValue: v ?? 0 })}
            placeholder={formData.discountType === 'percentage' ? '1-100' : 'Amount in cents'}
            required
            data-testid="input-discount-value"
          />
          <p className="text-xs text-muted-foreground">
            {formData.discountType === 'percentage' ? 'Percentage off (1-100)' : 'Amount in cents (e.g., 1000 = $10.00)'}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="freeTrialDays">Free Trial Days</Label>
        <NumberField
          id="freeTrialDays"
          min={0}
          emptyValue={0}
          value={formData.freeTrialDays}
          onValueChange={(v) => setFormData({ ...formData, freeTrialDays: v ?? 0 })}
          placeholder="0"
          data-testid="input-free-trial-days"
        />
        <p className="text-xs text-muted-foreground">Set to 0 for no trial period</p>
      </div>

      <div className="space-y-4 border rounded-lg p-4">
        <h4 className="font-medium">Plan Application</h4>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="applyToMonthly"
            checked={formData.applyToMonthly}
            onCheckedChange={(checked) => setFormData({ ...formData, applyToMonthly: checked as boolean })}
            data-testid="checkbox-apply-monthly"
          />
          <Label htmlFor="applyToMonthly">Apply to Monthly Plans</Label>
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="applyToAnnual"
            checked={formData.applyToAnnual}
            onCheckedChange={(checked) => setFormData({ ...formData, applyToAnnual: checked as boolean })}
            data-testid="checkbox-apply-annual"
          />
          <Label htmlFor="applyToAnnual">Apply to Annual Plans</Label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="monthlyDiscountOverride">Monthly Discount Override</Label>
          <Input
            id="monthlyDiscountOverride"
            type="number"
            min="0"
            value={formData.monthlyDiscountOverride}
            onChange={(e) => setFormData({ ...formData, monthlyDiscountOverride: e.target.value })}
            placeholder="Optional"
            data-testid="input-monthly-override"
          />
          <p className="text-xs text-muted-foreground">Override discount for monthly plans</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="annualDiscountOverride">Annual Discount Override</Label>
          <Input
            id="annualDiscountOverride"
            type="number"
            min="0"
            value={formData.annualDiscountOverride}
            onChange={(e) => setFormData({ ...formData, annualDiscountOverride: e.target.value })}
            placeholder="Optional"
            data-testid="input-annual-override"
          />
          <p className="text-xs text-muted-foreground">Override discount for annual plans</p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="maxRedemptions">Max Redemptions</Label>
        <Input
          id="maxRedemptions"
          type="number"
          min="0"
          value={formData.maxRedemptions}
          onChange={(e) => setFormData({ ...formData, maxRedemptions: e.target.value })}
          placeholder="Unlimited"
          data-testid="input-max-redemptions"
        />
        <p className="text-xs text-muted-foreground">Leave empty for unlimited redemptions</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="validFrom">Valid From *</Label>
          <Input
            id="validFrom"
            type="date"
            value={formData.validFrom}
            onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
            required
            data-testid="input-valid-from"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="validUntil">Valid Until</Label>
          <Input
            id="validUntil"
            type="date"
            value={formData.validUntil}
            onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
            data-testid="input-valid-until"
          />
          <p className="text-xs text-muted-foreground">Leave empty for no expiration</p>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          id="isActive"
          checked={formData.isActive}
          onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
          data-testid="switch-is-active"
        />
        <Label htmlFor="isActive">Is Active</Label>
      </div>

      <DialogFooter>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} data-testid="button-cancel-coupon">
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting} data-testid="button-submit-coupon">
          {isSubmitting ? "Saving..." : (coupon ? "Update Coupon" : "Create Coupon")}
        </Button>
      </DialogFooter>
    </form>
  );
}

// Helper function to generate random referral code
function generateReferralCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

// Affiliates Management Component
function AffiliatesManagement() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState<Affiliate | null>(null);
  const [deletingAffiliateId, setDeletingAffiliateId] = useState<string | null>(null);

  const { data: affiliates, isLoading } = useQuery<Affiliate[]>({
    queryKey: ["/api/affiliates"],
  });

  const createAffiliateMutation = useMutation({
    mutationFn: async (data: any) => {
      return await apiRequest("POST", "/api/affiliates", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/affiliates"] });
      setIsCreateDialogOpen(false);
      toast({ title: "Affiliate created successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Failed to create affiliate", variant: "destructive" });
    },
  });

  const updateAffiliateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return await apiRequest("PATCH", `/api/affiliates/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/affiliates"] });
      setEditingAffiliate(null);
      toast({ title: "Affiliate updated successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Failed to update affiliate", variant: "destructive" });
    },
  });

  const deleteAffiliateMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest("DELETE", `/api/affiliates/${id}`, null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/affiliates"] });
      setDeletingAffiliateId(null);
      toast({ title: "Affiliate deleted successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Failed to delete affiliate", variant: "destructive" });
    },
  });

  const formatCents = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          Affiliate Management
        </CardTitle>
        <CardDescription>
          Manage affiliate partners and track their referrals and earnings
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex justify-end mb-4">
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2" data-testid="button-create-affiliate">
                <Plus className="w-4 h-4" />
                Create Affiliate
              </Button>
            </DialogTrigger>
            <DialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
              <AffiliateForm 
                onSubmit={(data) => createAffiliateMutation.mutate(data)}
                onCancel={() => setIsCreateDialogOpen(false)}
                isSubmitting={createAffiliateMutation.isPending}
              />
            </DialogContent>
          </Dialog>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Referral Code</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Referrals</TableHead>
                  <TableHead>Conversions</TableHead>
                  <TableHead>Earned</TableHead>
                  <TableHead>Paid</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {affiliates?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      No affiliates created. Click "Create Affiliate" to get started.
                    </TableCell>
                  </TableRow>
                )}
                {affiliates?.map((affiliate) => (
                  <TableRow key={affiliate.id} data-testid={`row-affiliate-${affiliate.id}`}>
                    <TableCell>
                      <div className="font-medium">{affiliate.name}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{affiliate.email}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-mono font-medium">{affiliate.referralCode}</div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">
                        {affiliate.commissionType === 'percentage' 
                          ? `${affiliate.commissionValue}%` 
                          : formatCents(affiliate.commissionValue)}
                      </span>
                      <div className="text-xs text-muted-foreground">
                        {affiliate.commissionType === 'percentage' ? 'Percentage' : 'Fixed'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={affiliate.status === 'active' ? 'default' : affiliate.status === 'pending' ? 'outline' : 'secondary'} 
                        data-testid={`badge-affiliate-status-${affiliate.id}`}
                      >
                        {affiliate.status.charAt(0).toUpperCase() + affiliate.status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{affiliate.totalReferrals || 0}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{affiliate.totalConversions || 0}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium text-green-600">{formatCents(affiliate.totalEarned || 0)}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{formatCents(affiliate.totalPaid || 0)}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Dialog open={editingAffiliate?.id === affiliate.id} onOpenChange={(open) => !open && setEditingAffiliate(null)}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingAffiliate(affiliate)}
                              data-testid={`button-edit-affiliate-${affiliate.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
                            <AffiliateForm 
                              affiliate={affiliate}
                              onSubmit={(data) => updateAffiliateMutation.mutate({ id: affiliate.id, data })}
                              isSubmitting={updateAffiliateMutation.isPending}
                            />
                          </DialogContent>
                        </Dialog>
                        <Dialog open={deletingAffiliateId === affiliate.id} onOpenChange={(open) => !open && setDeletingAffiliateId(null)}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeletingAffiliateId(affiliate.id)}
                              data-testid={`button-delete-affiliate-${affiliate.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>Delete Affiliate</DialogTitle>
                              <DialogDescription>
                                Are you sure you want to delete the affiliate "{affiliate.name}"? This action cannot be undone.
                              </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setDeletingAffiliateId(null)} data-testid="button-cancel-delete-affiliate">
                                Cancel
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={() => deleteAffiliateMutation.mutate(affiliate.id)}
                                disabled={deleteAffiliateMutation.isPending}
                                data-testid="button-confirm-delete-affiliate"
                              >
                                {deleteAffiliateMutation.isPending ? "Deleting..." : "Delete"}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Affiliate Form Component
function AffiliateForm({ affiliate, onSubmit, onCancel, isSubmitting }: { affiliate?: Affiliate; onSubmit: (data: any) => void; onCancel?: () => void; isSubmitting: boolean }) {
  const [formData, setFormData] = useState({
    name: affiliate?.name || '',
    email: affiliate?.email || '',
    phone: affiliate?.phone || '',
    referralCode: affiliate?.referralCode || generateReferralCode(),
    commissionType: affiliate?.commissionType || 'percentage',
    commissionValue: affiliate?.commissionValue || 10,
    status: affiliate?.status || 'active',
    notes: affiliate?.notes || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Name is required');
      return;
    }
    if (!formData.email.trim()) {
      alert('Email is required');
      return;
    }
    if (!formData.referralCode.trim() || formData.referralCode.length < 4) {
      alert('Referral code must be at least 4 characters');
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <DialogHeader>
        <DialogTitle>{affiliate ? 'Edit Affiliate' : 'Create New Affiliate'}</DialogTitle>
        <DialogDescription>
          {affiliate ? 'Update the affiliate details below.' : 'Fill in the details to create a new affiliate partner.'}
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="affiliate-name">Name *</Label>
          <Input
            id="affiliate-name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="John Doe"
            required
            data-testid="input-affiliate-name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="affiliate-email">Email *</Label>
          <Input
            id="affiliate-email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="john@example.com"
            required
            data-testid="input-affiliate-email"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="affiliate-phone">Phone (Optional)</Label>
          <Input
            id="affiliate-phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+1 (555) 123-4567"
            data-testid="input-affiliate-phone"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="affiliate-referral-code">Referral Code *</Label>
          <div className="flex gap-2">
            <Input
              id="affiliate-referral-code"
              value={formData.referralCode}
              onChange={(e) => setFormData({ ...formData, referralCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
              placeholder="ABC12345"
              required
              data-testid="input-affiliate-referral-code"
            />
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setFormData({ ...formData, referralCode: generateReferralCode() })}
              data-testid="button-generate-code"
            >
              Generate
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Unique code for referral tracking</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="affiliate-commission-type">Commission Type *</Label>
          <Select 
            value={formData.commissionType} 
            onValueChange={(value) => setFormData({ ...formData, commissionType: value })}
          >
            <SelectTrigger id="affiliate-commission-type" data-testid="select-affiliate-commission-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="percentage">Percentage</SelectItem>
              <SelectItem value="fixed">Fixed Amount</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="affiliate-commission-value">Commission Value *</Label>
          <NumberField
            id="affiliate-commission-value"
            min={0}
            max={formData.commissionType === 'percentage' ? 100 : undefined}
            allowDecimal={formData.commissionType === 'percentage'}
            emptyValue={0}
            value={formData.commissionValue}
            onValueChange={(v) => setFormData({ ...formData, commissionValue: v ?? 0 })}
            placeholder={formData.commissionType === 'percentage' ? '1-100' : 'Amount in cents'}
            required
            data-testid="input-affiliate-commission-value"
          />
          <p className="text-xs text-muted-foreground">
            {formData.commissionType === 'percentage' ? 'Percentage per sale (1-100)' : 'Amount in cents per sale (e.g., 1000 = $10.00)'}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="affiliate-status">Status *</Label>
        <Select 
          value={formData.status} 
          onValueChange={(value) => setFormData({ ...formData, status: value })}
        >
          <SelectTrigger id="affiliate-status" data-testid="select-affiliate-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="affiliate-notes">Notes (Optional)</Label>
        <Textarea
          id="affiliate-notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Additional notes about this affiliate..."
          rows={3}
          data-testid="input-affiliate-notes"
        />
      </div>

      <DialogFooter>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} data-testid="button-cancel-affiliate">
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting} data-testid="button-submit-affiliate">
          {isSubmitting ? "Saving..." : (affiliate ? "Update Affiliate" : "Create Affiliate")}
        </Button>
      </DialogFooter>
    </form>
  );
}

// AI Pricing Management Component
function AiPricingManagement() {
  const queryClient = useQueryClient();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<AiPricingTier | null>(null);
  const [deletingTierId, setDeletingTierId] = useState<string | null>(null);

  const { data: aiPricingTiers, isLoading } = useQuery<AiPricingTier[]>({
    queryKey: ["/api/reseller/ai-pricing"],
  });

  const createTierMutation = useMutation({
    mutationFn: async (data: any) => {
      return await apiRequest("POST", "/api/reseller/ai-pricing", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reseller/ai-pricing"] });
      setIsCreateDialogOpen(false);
    },
    onError: (error: any) => {
    },
  });

  const updateTierMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return await apiRequest("PATCH", `/api/reseller/ai-pricing/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reseller/ai-pricing"] });
      setEditingTier(null);
    },
    onError: (error: any) => {
    },
  });

  const deleteTierMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest("DELETE", `/api/reseller/ai-pricing/${id}`, null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reseller/ai-pricing"] });
      setDeletingTierId(null);
    },
    onError: (error: any) => {
    },
  });

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5" />
            AI Pricing Tiers (Replit Model)
          </CardTitle>
          <CardDescription>
            Configure AI pricing with monthly minimums plus variable token usage charges
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-end mb-4">
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="flex items-center gap-2" data-testid="button-add-ai-tier">
                  <Plus className="w-4 h-4" />
                  Add Pricing Tier
                </Button>
              </DialogTrigger>
              <DialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
                <AiPricingTierForm 
                  onSubmit={(data) => createTierMutation.mutate(data)}
                  isSubmitting={createTierMutation.isPending}
                />
              </DialogContent>
            </Dialog>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tier</TableHead>
                    <TableHead>Monthly Minimum</TableHead>
                    <TableHead>Tokens Included</TableHead>
                    <TableHead>Price per 1K Tokens</TableHead>
                    <TableHead>Features</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {aiPricingTiers?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No AI pricing tiers configured. Click "Add Pricing Tier" to get started.
                      </TableCell>
                    </TableRow>
                  )}
                  {aiPricingTiers?.map((tier) => (
                    <TableRow key={tier.id} data-testid={`row-ai-tier-${tier.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="font-medium">{tier.displayName}</div>
                          <div className="text-sm text-muted-foreground">{tier.name}</div>
                        </div>
                        {tier.isRecommended && (
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            Recommended
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">${tier.monthlyMinimum}</span>
                      <div className="text-sm text-muted-foreground">per month</div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{tier.tokensIncluded.toLocaleString()}</span>
                      <div className="text-sm text-muted-foreground">tokens/month</div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">${tier.pricePerThousandTokens}</span>
                      <div className="text-sm text-muted-foreground">per 1K tokens</div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {tier.enableJournalAI && (
                          <Badge variant="outline" className="text-xs">Journal AI</Badge>
                        )}
                        {tier.enableGoalSuggestions && (
                          <Badge variant="outline" className="text-xs">Goal Suggestions</Badge>
                        )}
                        {tier.enableInsights && (
                          <Badge variant="outline" className="text-xs">Insights</Badge>
                        )}
                        {tier.enableAdvancedAnalytics && (
                          <Badge variant="outline" className="text-xs">Advanced Analytics</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={tier.isActive ? "default" : "secondary"}>
                        {tier.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Dialog open={editingTier?.id === tier.id} onOpenChange={(open) => !open && setEditingTier(null)}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingTier(tier)}
                              data-testid={`button-edit-tier-${tier.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
                            <AiPricingTierForm 
                              tier={tier}
                              onSubmit={(data) => updateTierMutation.mutate({ id: tier.id, data })}
                              isSubmitting={updateTierMutation.isPending}
                            />
                          </DialogContent>
                        </Dialog>
                        <Dialog open={deletingTierId === tier.id} onOpenChange={(open) => !open && setDeletingTierId(null)}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeletingTierId(tier.id)}
                              data-testid={`button-delete-tier-${tier.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>Delete AI Pricing Tier</DialogTitle>
                              <DialogDescription>
                                Are you sure you want to delete the tier "{tier.displayName}"? This action cannot be undone.
                              </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setDeletingTierId(null)} data-testid="button-cancel-delete-tier">
                                Cancel
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={() => deleteTierMutation.mutate(tier.id)}
                                disabled={deleteTierMutation.isPending}
                                data-testid="button-confirm-delete-tier"
                              >
                                {deleteTierMutation.isPending ? "Deleting..." : "Delete"}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Usage Limits & Features Explanation</CardTitle>
          <CardDescription>
            Configure rate limits and feature availability for each tier in the form above
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <h4 className="font-medium">Rate Limits</h4>
              <p className="text-sm text-muted-foreground">
                Control API request frequency (per minute/day) to prevent abuse and manage costs. Set when creating/editing tiers.
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium">AI Features</h4>
              <p className="text-sm text-muted-foreground">
                Enable specific AI capabilities (Journal AI, Goal Suggestions, Insights, Analytics) for different pricing tiers.
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium">Billing Model</h4>
              <p className="text-sm text-muted-foreground">
                Monthly minimum charge + variable token usage (like Replit). Users pay the minimum, then extra for tokens beyond included amount.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

// AI Pricing Tier Form Component
function AiPricingTierForm({ tier, onSubmit, isSubmitting }: { tier?: AiPricingTier; onSubmit: (data: any) => void; isSubmitting: boolean }) {
  const [formData, setFormData] = useState({
    name: tier?.name || '',
    displayName: tier?.displayName || '',
    description: tier?.description || '',
    monthlyMinimum: tier?.monthlyMinimum || '0.00',
    tokensIncluded: tier?.tokensIncluded || 0,
    pricePerThousandTokens: tier?.pricePerThousandTokens || '0.0000',
    maxTokensPerMonth: tier?.maxTokensPerMonth || '',
    maxRequestsPerMinute: tier?.maxRequestsPerMinute || 10,
    maxRequestsPerDay: tier?.maxRequestsPerDay || 100,
    enableJournalAI: tier?.enableJournalAI ?? false,
    enableGoalSuggestions: tier?.enableGoalSuggestions ?? false,
    enableInsights: tier?.enableInsights ?? false,
    enableAdvancedAnalytics: tier?.enableAdvancedAnalytics ?? false,
    priority: tier?.priority || 0,
    isActive: tier?.isActive ?? true,
    isRecommended: tier?.isRecommended ?? false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate name format
    const name = formData.name.toLowerCase().replace(/[^a-z0-9_-]/g, '');
    if (name.length < 2) {
      alert('Tier name must be at least 2 characters long (alphanumeric, underscore, hyphen)');
      return;
    }

    // Prepare data for submission
    const submitData: any = {
      ...formData,
      name,
      maxTokensPerMonth: formData.maxTokensPerMonth ? parseInt(formData.maxTokensPerMonth as string) : null,
    };

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <DialogHeader>
        <DialogTitle>{tier ? 'Edit AI Pricing Tier' : 'Create AI Pricing Tier'}</DialogTitle>
        <DialogDescription>
          {tier ? 'Update the AI pricing tier details below.' : 'Configure a new AI pricing tier with monthly minimum and token pricing.'}
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Tier Name (Internal) *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })}
            placeholder="pro"
            required
            data-testid="input-tier-name"
          />
          <p className="text-xs text-muted-foreground">Lowercase alphanumeric, underscore, hyphen only</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="displayName">Display Name *</Label>
          <Input
            id="displayName"
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            placeholder="Pro Plan"
            required
            data-testid="input-tier-display-name"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Perfect for growing teams..."
          data-testid="input-tier-description"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="monthlyMinimum">Monthly Minimum ($) *</Label>
          <Input
            id="monthlyMinimum"
            type="number"
            step="0.01"
            min="0"
            value={formData.monthlyMinimum}
            onChange={(e) => setFormData({ ...formData, monthlyMinimum: e.target.value })}
            placeholder="0.00"
            required
            data-testid="input-monthly-minimum"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="tokensIncluded">Tokens Included *</Label>
          <NumberField
            id="tokensIncluded"
            min={0}
            emptyValue={0}
            value={formData.tokensIncluded}
            onValueChange={(v) => setFormData({ ...formData, tokensIncluded: v ?? 0 })}
            placeholder="10000"
            required
            data-testid="input-tokens-included"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="pricePerThousandTokens">Price per 1K Tokens ($) *</Label>
          <Input
            id="pricePerThousandTokens"
            type="number"
            step="0.0001"
            min="0"
            value={formData.pricePerThousandTokens}
            onChange={(e) => setFormData({ ...formData, pricePerThousandTokens: e.target.value })}
            placeholder="0.0200"
            required
            data-testid="input-price-per-thousand"
          />
        </div>
      </div>

      <div className="space-y-4 border rounded-lg p-4">
        <h4 className="font-medium">Usage Limits</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="maxRequestsPerMinute">Requests/Minute *</Label>
            <NumberField
              id="maxRequestsPerMinute"
              min={1}
              emptyValue={10}
              value={formData.maxRequestsPerMinute}
              onValueChange={(v) => setFormData({ ...formData, maxRequestsPerMinute: v ?? 10 })}
              placeholder="10"
              required
              data-testid="input-max-requests-minute"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="maxRequestsPerDay">Requests/Day *</Label>
            <NumberField
              id="maxRequestsPerDay"
              min={1}
              emptyValue={100}
              value={formData.maxRequestsPerDay}
              onValueChange={(v) => setFormData({ ...formData, maxRequestsPerDay: v ?? 100 })}
              placeholder="100"
              required
              data-testid="input-max-requests-day"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="maxTokensPerMonth">Max Tokens/Month</Label>
            <Input
              id="maxTokensPerMonth"
              type="number"
              min="0"
              value={formData.maxTokensPerMonth}
              onChange={(e) => setFormData({ ...formData, maxTokensPerMonth: e.target.value })}
              placeholder="Unlimited"
              data-testid="input-max-tokens-month"
            />
            <p className="text-xs text-muted-foreground">Leave empty for unlimited</p>
          </div>
        </div>
      </div>

      <div className="space-y-4 border rounded-lg p-4">
        <h4 className="font-medium">AI Features</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="enableJournalAI"
              checked={formData.enableJournalAI}
              onCheckedChange={(checked) => setFormData({ ...formData, enableJournalAI: checked as boolean })}
              data-testid="checkbox-journal-ai"
            />
            <Label htmlFor="enableJournalAI">AI Journal Insights</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="enableGoalSuggestions"
              checked={formData.enableGoalSuggestions}
              onCheckedChange={(checked) => setFormData({ ...formData, enableGoalSuggestions: checked as boolean })}
              data-testid="checkbox-goal-suggestions"
            />
            <Label htmlFor="enableGoalSuggestions">Smart Goal Suggestions</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="enableInsights"
              checked={formData.enableInsights}
              onCheckedChange={(checked) => setFormData({ ...formData, enableInsights: checked as boolean })}
              data-testid="checkbox-insights"
            />
            <Label htmlFor="enableInsights">Productivity Insights</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="enableAdvancedAnalytics"
              checked={formData.enableAdvancedAnalytics}
              onCheckedChange={(checked) => setFormData({ ...formData, enableAdvancedAnalytics: checked as boolean })}
              data-testid="checkbox-advanced-analytics"
            />
            <Label htmlFor="enableAdvancedAnalytics">Advanced Analytics</Label>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="priority">Display Priority *</Label>
          <NumberField
            id="priority"
            min={0}
            emptyValue={0}
            value={formData.priority}
            onValueChange={(v) => setFormData({ ...formData, priority: v ?? 0 })}
            placeholder="0"
            required
            data-testid="input-priority"
          />
          <p className="text-xs text-muted-foreground">Lower = higher priority</p>
        </div>

        <div className="flex items-center space-x-2 pt-7">
          <Switch
            id="isActive"
            checked={formData.isActive}
            onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
            data-testid="switch-is-active"
          />
          <Label htmlFor="isActive">Is Active</Label>
        </div>

        <div className="flex items-center space-x-2 pt-7">
          <Switch
            id="isRecommended"
            checked={formData.isRecommended}
            onCheckedChange={(checked) => setFormData({ ...formData, isRecommended: checked })}
            data-testid="switch-is-recommended"
          />
          <Label htmlFor="isRecommended">Recommended</Label>
        </div>
      </div>

      <DialogFooter>
        <Button type="submit" disabled={isSubmitting} data-testid="button-submit-tier">
          {isSubmitting ? "Saving..." : (tier ? "Update Tier" : "Create Tier")}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function ResellerPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("branding");

  const { data: config, isLoading } = useQuery<ResellerConfig>({
    queryKey: ["/api/reseller/config"],
  });

  const form = useForm<ResellerConfigForm>({
    resolver: zodResolver(resellerConfigSchema),
    defaultValues: {
      companyName: config?.companyName || "GoalVia",
      companyLogo: config?.companyLogo || "",
      heroTitle: config?.heroTitle || "Transform Your Team's Productivity",
      heroSubtitle: config?.heroSubtitle || "The comprehensive productivity platform that combines goal tracking, habit formation, journaling, and calendar management to help teams achieve more together.",
      primaryColor: config?.primaryColor || "#3b82f6",
      secondaryColor: config?.secondaryColor || "#1e293b",
      showPricing: config?.showPricing ?? true,
      customPricingMessage: config?.customPricingMessage || "",
      supportEmail: config?.supportEmail || "",
      website: config?.website || "",
      termsUrl: config?.termsUrl || "",
      privacyUrl: config?.privacyUrl || "",
      stripeAccountId: config?.stripeAccountId || "",
      enableCustomDomains: config?.enableCustomDomains ?? false,
      enableWhiteLabeling: config?.enableWhiteLabeling ?? false,
    },
  });

  // Reset form when config loads
  useEffect(() => {
    if (config) {
      form.reset({
        companyName: config.companyName,
        companyLogo: config.companyLogo || "",
        heroTitle: config.heroTitle,
        heroSubtitle: config.heroSubtitle,
        primaryColor: config.primaryColor,
        secondaryColor: config.secondaryColor,
        showPricing: config.showPricing,
        customPricingMessage: config.customPricingMessage || "",
        supportEmail: config.supportEmail || "",
        website: config.website || "",
        termsUrl: config.termsUrl || "",
        privacyUrl: config.privacyUrl || "",
        stripeAccountId: config.stripeAccountId || "",
        enableCustomDomains: config.enableCustomDomains,
        enableWhiteLabeling: config.enableWhiteLabeling,
      });
    }
  }, [config, form]);

  const updateConfigMutation = useMutation({
    mutationFn: async (data: ResellerConfigForm) => {
      return await apiRequest("POST", "/api/reseller/config", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reseller/config"] });
    },
    onError: (error: any) => {
    },
  });

  const onSubmit = (data: ResellerConfigForm) => {
    updateConfigMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-full p-4 md:p-6 space-y-8">
      <div className="flex items-center space-x-4">
        <Store className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold text-foreground">Sales & Storefront</h1>
          <p className="text-muted-foreground">Manage pricing plans, affiliates, coupons, and storefront branding</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 md:grid-cols-7">
              <TabsTrigger value="branding" className="flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Branding
              </TabsTrigger>
              <TabsTrigger value="pricing" className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Pricing
              </TabsTrigger>
              <TabsTrigger value="ai-pricing" className="flex items-center gap-2">
                <Bot className="w-4 h-4" />
                AI Pricing
              </TabsTrigger>
              <TabsTrigger value="coupons" className="flex items-center gap-2">
                <Ticket className="w-4 h-4" />
                Coupons
              </TabsTrigger>
              <TabsTrigger value="affiliates" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Affiliates
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Settings
              </TabsTrigger>
              <TabsTrigger value="preview" className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Preview
              </TabsTrigger>
            </TabsList>

            <TabsContent value="branding" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Brand Identity</CardTitle>
                  <CardDescription>
                    Configure your company branding and visual identity
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="companyName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Company Name</FormLabel>
                          <FormControl>
                            <Input placeholder="GoalVia" {...field} data-testid="input-company-name" />
                          </FormControl>
                          <FormDescription>
                            This will appear in the header and throughout the platform
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="companyLogo"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Company Logo URL</FormLabel>
                          <FormControl>
                            <Input placeholder="https://example.com/logo.png" {...field} data-testid="input-company-logo" />
                          </FormControl>
                          <FormDescription>
                            URL to your company logo (optional)
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="primaryColor"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Primary Color</FormLabel>
                          <FormControl>
                            <div className="flex items-center space-x-2">
                              <Input type="color" {...field} className="w-16 h-10" data-testid="input-primary-color" />
                              <Input placeholder="#3b82f6" {...field} data-testid="input-primary-color-text" />
                            </div>
                          </FormControl>
                          <FormDescription>
                            Main brand color for buttons and accents
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="secondaryColor"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Secondary Color</FormLabel>
                          <FormControl>
                            <div className="flex items-center space-x-2">
                              <Input type="color" {...field} className="w-16 h-10" data-testid="input-secondary-color" />
                              <Input placeholder="#1e293b" {...field} data-testid="input-secondary-color-text" />
                            </div>
                          </FormControl>
                          <FormDescription>
                            Secondary color for backgrounds and borders
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>


            <TabsContent value="pricing" className="space-y-6">
              <PricingManagement />
            </TabsContent>

            <TabsContent value="ai-pricing" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bot className="w-5 h-5" />
                    AI Pricing &amp; Token Limits
                  </CardTitle>
                  <CardDescription>
                    AI token pricing, rate limits, and feature flags are now managed directly inside each pricing plan.
                    Go to <strong>Pricing Plans → Edit a plan → AI &amp; Usage Costs tab</strong> to configure them in one place.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md bg-muted/40 border p-4 flex items-start gap-3 mb-4">
                    <Info className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      Each pricing plan now has a combined <strong>"AI &amp; Usage Costs"</strong> tab that lets you set
                      the upcharge calculator (what you charge customers for AI and storage) alongside the actual
                      AI token limits, overage pricing, and feature flags — all in one place.
                      Legacy tiers created before this change are shown below.
                    </p>
                  </div>
                  <AiPricingManagement />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="coupons" className="space-y-6">
              <CouponsManagement />
            </TabsContent>

            <TabsContent value="affiliates" className="space-y-6">
              <AffiliatesManagement />
            </TabsContent>

            <TabsContent value="settings" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Contact & Legal</CardTitle>
                  <CardDescription>
                    Configure contact information and legal page links
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="supportEmail"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Support Email</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="support@yourcompany.com" {...field} data-testid="input-support-email" />
                          </FormControl>
                          <FormDescription>
                            Email address for customer support
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="website"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Company Website</FormLabel>
                          <FormControl>
                            <Input placeholder="https://yourcompany.com" {...field} data-testid="input-website" />
                          </FormControl>
                          <FormDescription>
                            Your main company website
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="termsUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Terms of Service URL</FormLabel>
                          <FormControl>
                            <Input placeholder="https://yourcompany.com/terms" {...field} data-testid="input-terms-url" />
                          </FormControl>
                          <FormDescription>
                            Link to your terms of service
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="privacyUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Privacy Policy URL</FormLabel>
                          <FormControl>
                            <Input placeholder="https://yourcompany.com/privacy" {...field} data-testid="input-privacy-url" />
                          </FormControl>
                          <FormDescription>
                            Link to your privacy policy
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Payment Integration</CardTitle>
                  <CardDescription>
                    Configure payment provider settings for your reseller account
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <FormField
                    control={form.control}
                    name="stripeAccountId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Stripe Connect Account ID</FormLabel>
                        <FormControl>
                          <Input placeholder="acct_..." {...field} data-testid="input-stripe-account-id" />
                        </FormControl>
                        <FormDescription>
                          Your Stripe Connect account ID for receiving payments (leave empty to use platform account)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Advanced Features</CardTitle>
                  <CardDescription>
                    Enable advanced reseller capabilities
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <FormField
                    control={form.control}
                    name="enableCustomDomains"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Custom Domains</FormLabel>
                          <FormDescription>
                            Allow customers to use their own custom domains
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} data-testid="switch-custom-domains" />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="enableWhiteLabeling"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Complete White Labeling</FormLabel>
                          <FormDescription>
                            Remove all GoalVia branding and use only your brand
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} data-testid="switch-white-labeling" />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="preview" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Pricing Page Preview</CardTitle>
                  <CardDescription>
                    Live preview of your public pricing page as visitors will see it
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0 overflow-hidden rounded-b-lg">
                  <iframe
                    src="/publicpricing"
                    className="w-full border-0"
                    style={{ height: "700px" }}
                    title="Pricing Page Preview"
                    data-testid="iframe-pricing-preview"
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => form.reset()}
              data-testid="button-reset-config"
            >
              Reset Changes
            </Button>
            <Button
              type="submit"
              disabled={updateConfigMutation.isPending}
              data-testid="button-save-config"
            >
              {updateConfigMutation.isPending ? "Saving..." : "Save Configuration"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

// Plan Features Editor Component for dialog form
function StructuredPlanFeaturesEditor({ 
  planFeatures, 
  onChange 
}: { 
  planFeatures: PlanFeatures; 
  onChange: (planFeatures: PlanFeatures) => void;
}) {
  // Local display state for limit inputs so they don't snap back to 0 while typing.
  // Strategy: update the display string on every keystroke; push a valid number to
  // the form immediately when available; on blur normalise any remaining invalid text.
  // This avoids race conditions between onBlur and the Save button's onClick.
  const [limitDisplays, setLimitDisplays] = useState<Record<string, string>>({});

  const updateMenuAccess = (key: string, value: boolean) => {
    onChange({
      ...planFeatures,
      menuAccess: { ...planFeatures.menuAccess, [key]: value }
    });
  };

  const handleLimitChange = (key: string, rawValue: string) => {
    // Always update the visible string so typing feels responsive
    setLimitDisplays(prev => ({ ...prev, [key]: rawValue }));
    // Push a valid integer to the form immediately so Save always reads the latest
    const parsed = parseInt(rawValue, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      onChange({
        ...planFeatures,
        limits: { ...planFeatures.limits, [key]: parsed }
      });
    }
  };

  const handleLimitBlur = (key: string, rawValue: string) => {
    // Normalise on blur: empty / negative / NaN → 0
    const parsed = parseInt(rawValue, 10);
    const finalValue = isNaN(parsed) ? 0 : Math.max(0, parsed);
    setLimitDisplays(prev => { const n = { ...prev }; delete n[key]; return n; });
    onChange({
      ...planFeatures,
      limits: { ...planFeatures.limits, [key]: finalValue }
    });
  };

  const updateToggle = (key: string, value: boolean) => {
    onChange({
      ...planFeatures,
      toggles: { ...planFeatures.toggles, [key]: value }
    });
  };

  const getLimitDisplayValue = (limit: string): string => {
    if (limitDisplays[limit] !== undefined) return limitDisplays[limit];
    const committed = (planFeatures.limits as any)[limit];
    return committed !== undefined ? String(committed) : '0';
  };

  return (
    <div className="space-y-6" data-testid="structured-plan-features">
      {/* Menu Access Section */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Menu Access</Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 border rounded-md">
          {FEATURE_REGISTRY.menuItems.map((item) => (
            <div key={item} className="flex items-center space-x-2">
              <Checkbox
                id={`menu-${item}`}
                checked={planFeatures.menuAccess[item] || false}
                onCheckedChange={(checked) => updateMenuAccess(item, checked as boolean)}
                data-testid={`checkbox-menu-${item}`}
              />
              <label
                htmlFor={`menu-${item}`}
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 capitalize"
              >
                {item.replace(/_/g, ' ')}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Limits Section */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Resource Limits</Label>
        <p className="text-xs text-muted-foreground">Set to 0 for unlimited. Press Tab or click away to confirm each value.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-md">
          {FEATURE_REGISTRY.limits.map((limit) => (
            <div key={limit} className="space-y-2">
              <Label htmlFor={`limit-${limit}`} className="text-sm capitalize">
                {limit.replace('max_', '').replace(/_/g, ' ')}
              </Label>
              <Input
                id={`limit-${limit}`}
                type="number"
                min="0"
                value={getLimitDisplayValue(limit)}
                onChange={(e) => handleLimitChange(limit, e.target.value)}
                onBlur={(e) => handleLimitBlur(limit, e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                data-testid={`input-limit-${limit}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Toggles Section */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Feature Toggles</Label>
        <div className="space-y-3 p-4 border rounded-md">
          {FEATURE_REGISTRY.toggles.map((toggle) => (
            <div key={toggle} className="flex items-center justify-between">
              <label htmlFor={`toggle-${toggle}`} className="text-sm font-medium capitalize">
                {toggle.replace(/_/g, ' ')}
              </label>
              <Switch
                id={`toggle-${toggle}`}
                checked={planFeatures.toggles[toggle] || false}
                onCheckedChange={(checked) => updateToggle(toggle, checked)}
                data-testid={`switch-toggle-${toggle}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Visual Plan Card Component for inline editing
function VisualPlanCard({ 
  plan, 
  onEdit, 
  onDelete, 
  onUpdate, 
  isDeleting, 
  isUpdating 
}: {
  plan: SubscriptionPlan;
  onEdit: (plan: SubscriptionPlan) => void;
  onDelete: () => void;
  onUpdate: (data: any) => void;
  isDeleting: boolean;
  isUpdating: boolean;
}) {
  const [editingFeatures, setEditingFeatures] = useState(false);
  const [features, setFeatures] = useState<string[]>(((plan as { features?: unknown }).features as string[]) || []);
  const [newFeature, setNewFeature] = useState("");

  const addFeature = () => {
    if (newFeature.trim() && !features.includes(newFeature.trim())) {
      const updatedFeatures = [...features, newFeature.trim()];
      setFeatures(updatedFeatures);
      setNewFeature("");
    }
  };

  const removeFeature = (index: number) => {
    const updatedFeatures = features.filter((_, i) => i !== index);
    setFeatures(updatedFeatures);
  };

  const saveFeatures = () => {
    onUpdate({ ...plan, features });
    setEditingFeatures(false);
  };

  const cancelEditFeatures = () => {
    setFeatures(((plan as { features?: unknown }).features as string[]) || []);
    setEditingFeatures(false);
    setNewFeature("");
  };

  return (
    <div 
      className={`border rounded-lg p-6 relative transition-all hover:shadow-md ${
        plan.isFeatured ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'border-border'
      } ${!plan.isActive ? 'opacity-60' : ''}`}
      data-testid={`visual-plan-card-${plan.id}`}
    >
      {/* Featured Badge */}
      {plan.isFeatured && (
        <Badge className="absolute -top-3 left-4 bg-primary text-primary-foreground">
          <Star className="w-3 h-3 mr-1" />
          Most Popular
        </Badge>
      )}

      {/* Landing Page Position Badge */}
      {plan.landingPagePosition && (
        <Badge variant="outline" className="absolute -top-3 right-4 bg-background">
          Plan {plan.landingPagePosition}
        </Badge>
      )}

      {/* Status Badge */}
      {!plan.isActive && (
        <Badge variant="outline" className={`absolute -top-3 ${plan.landingPagePosition ? 'right-20' : 'right-4'}`}>
          Inactive
        </Badge>
      )}

      {/* Plan Header */}
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold mb-2">{plan.displayName}</h3>
        <div className="mb-3">
          <span className="text-3xl font-bold">
            {plan.currency} {(plan.price / 100).toFixed(2)}
          </span>
          <span className="text-muted-foreground text-lg">
            /{plan.interval}
          </span>
        </div>
        {plan.description && (
          <p className="text-sm text-muted-foreground mb-4">
            {plan.description}
          </p>
        )}
      </div>

      {/* Storage & Upcharges Info */}
      <div className="mb-6 space-y-2 bg-muted/50 rounded-lg p-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Storage Limit:</span>
          <span className="font-medium">{plan.storageLimitMb ?? 100} MB</span>
        </div>
        {(plan.aiUpcharge ?? 0) > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">AI Upcharge:</span>
            <span className="font-medium">${((plan.aiUpcharge ?? 0) / 100).toFixed(2)}</span>
          </div>
        )}
        {(plan.mediaStorageUpcharge ?? 0) > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Media Storage Upcharge:</span>
            <span className="font-medium">${((plan.mediaStorageUpcharge ?? 0) / 100).toFixed(2)}</span>
          </div>
        )}
        {(plan.planFeatures as any)?.limits && Object.keys((plan.planFeatures as any).limits).length > 0 && (
          <div className="pt-2 border-t border-border/50">
            <div className="text-xs font-medium text-muted-foreground mb-2">Resource Limits:</div>
            {Object.entries((plan.planFeatures as any).limits).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}:</span>
                <span className="font-medium">{value as number}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Features Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-sm">Features</h4>
          {!editingFeatures ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditingFeatures(true)}
              data-testid={`button-edit-features-${plan.id}`}
            >
              <Edit className="w-3 h-3 mr-1" />
              Edit
            </Button>
          ) : (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={saveFeatures}
                disabled={isUpdating}
                data-testid={`button-save-features-${plan.id}`}
              >
                <Check className="w-3 h-3 mr-1" />
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={cancelEditFeatures}
                data-testid={`button-cancel-features-${plan.id}`}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-2 min-h-[120px]">
          {features.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No features added</p>
          ) : (
            features.map((feature, index) => (
              <div 
                key={index} 
                className="flex items-center gap-2 text-sm"
                data-testid={`feature-${plan.id}-${index}`}
              >
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span className="flex-1">{feature}</span>
                {editingFeatures && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFeature(index)}
                    className="h-6 w-6 p-0"
                    data-testid={`button-remove-feature-${plan.id}-${index}`}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            ))
          )}

          {editingFeatures && (
            <div className="flex gap-2 mt-3">
              <Input
                placeholder="Add new feature..."
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                className="text-sm"
                data-testid={`input-new-feature-${plan.id}`}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={addFeature}
                disabled={!newFeature.trim()}
                data-testid={`button-add-feature-${plan.id}`}
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => onEdit(plan)}
          data-testid={`button-edit-plan-${plan.id}`}
        >
          <Edit className="w-3 h-3 mr-1" />
          Edit Details
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onDelete}
          disabled={isDeleting}
          data-testid={`button-delete-plan-${plan.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

// Inline editable cell for the Compare Plans table
export function InlineEditCell({ value, type, onSave, min, emptyValue }: { value: string; type: 'text' | 'number'; onSave: (v: string) => void; min?: number; emptyValue?: number }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value);

  useEffect(() => { if (!editing) setLocal(value); }, [value, editing]);

  // Numeric state derived from the raw text while editing (decimal-aware)
  const parsed = parseFloat(local);
  const isEmpty = local.trim() === '' || Number.isNaN(parsed);
  const belowMin = !isEmpty && min !== undefined && parsed < min;

  const commit = () => {
    setEditing(false);
    if (type === 'number') {
      // Keep the raw text while editing; normalize only on commit. Empty or
      // invalid input commits emptyValue (with an inline hint shown while
      // editing) instead of silently snapping to 0; when no emptyValue is
      // configured, an empty edit reverts to the previous value.
      const p = parseFloat(local);
      let next: number | undefined;
      if (Number.isNaN(p)) {
        next = emptyValue;
      } else {
        next = min !== undefined ? Math.max(min, p) : p;
      }
      if (next === undefined) { setLocal(value); return; }
      const s = String(next);
      setLocal(s);
      if (s !== value) onSave(s);
    } else if (local !== value) {
      // An empty text field has no emptyValue fallback for text-type cells;
      // silently saving "" would wipe a plan name. Revert to the original
      // value instead, matching the number-type behaviour when emptyValue is
      // not configured.
      if (local.trim() === '') { setLocal(value); return; }
      onSave(local);
    }
  };

  return editing ? (
    <div className="min-w-[80px]">
      <input
        autoFocus
        type={type}
        min={min}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') { setLocal(value); setEditing(false); }
        }}
        className="w-full border border-primary rounded px-2 py-1 text-sm bg-background focus:outline-none min-w-[80px]"
      />
      {type === 'number' && isEmpty && emptyValue !== undefined && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5" data-testid="inline-edit-empty-hint">
          Enter a number — will be saved as {emptyValue} if left empty.
        </p>
      )}
      {type === 'number' && belowMin && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5" data-testid="inline-edit-clamp-hint">
          Must be at least {min} — will be saved as {min}.
        </p>
      )}
    </div>
  ) : (
    <span
      onClick={() => setEditing(true)}
      className="cursor-text text-sm px-2 py-1 rounded border border-transparent hover:border-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors block min-w-[80px]"
    >
      {local || <span className="text-muted-foreground">—</span>}
    </span>
  );
}

// Compare Plans tab — side-by-side inline editing of up to 3 plans
export function ComparePlansTab({
  currentPlan,
  plans,
  getFormValue,
  setFormValue,
  onUpdateOtherPlan,
  _testInitialCompareIds,
}: {
  currentPlan: SubscriptionPlan;
  plans: SubscriptionPlan[];
  getFormValue: (field: string) => any;
  setFormValue: (field: string, value: any) => void;
  onUpdateOtherPlan: (planId: string, data: Record<string, any>) => void;
  /** Test-only: pre-selects comparison plan IDs to bypass Radix Select interaction in unit tests. */
  _testInitialCompareIds?: [string, string];
}) {
  const [compareIds, setCompareIds] = useState<[string, string]>(_testInitialCompareIds ?? ["", ""]);
  const available = plans.filter(p => p.id !== currentPlan.id);
  const plan1 = plans.find(p => p.id === compareIds[0]) || null;
  const plan2 = plans.find(p => p.id === compareIds[1]) || null;

  const getVal = (plan: SubscriptionPlan | null, isCurrent: boolean, field: string) => {
    if (isCurrent) return getFormValue(field) ?? '';
    return plan ? (plan as any)[field] ?? '' : null;
  };

  const getFeature = (plan: SubscriptionPlan | null, isCurrent: boolean, section: 'menuAccess' | 'limits' | 'toggles', key: string) => {
    const features = isCurrent
      ? getFormValue('planFeatures') || {}
      : (plan?.planFeatures as any) || {};
    const val = features[section]?.[key];
    return val ?? (section === 'limits' ? 0 : false);
  };

  const saveCurrentFeature = (section: 'menuAccess' | 'limits' | 'toggles', key: string, value: any) => {
    const f = getFormValue('planFeatures') || { menuAccess: {}, limits: {}, toggles: {} };
    setFormValue('planFeatures', { ...f, [section]: { ...(f[section] || {}), [key]: value } });
  };

  const saveOtherFeature = (plan: SubscriptionPlan, section: 'menuAccess' | 'limits' | 'toggles', key: string, value: any) => {
    const f = (plan.planFeatures as any) || { menuAccess: {}, limits: {}, toggles: {} };
    onUpdateOtherPlan(plan.id, { planFeatures: { ...f, [section]: { ...(f[section] || {}), [key]: value } } });
  };

  const renderValueCell = (plan: SubscriptionPlan | null, isCurrent: boolean, field: string, type: 'text' | 'number' | 'boolean') => {
    if (!isCurrent && !plan) return <TableCell className="text-center text-muted-foreground">—</TableCell>;
    const raw = getVal(plan, isCurrent, field);
    if (type === 'boolean') {
      return (
        <TableCell className="text-center">
          <Switch
            checked={!!raw}
            onCheckedChange={(v) => {
              if (isCurrent) setFormValue(field, v);
              else if (plan) onUpdateOtherPlan(plan.id, { [field]: v });
            }}
          />
        </TableCell>
      );
    }
    const displayValue = field === 'price'
      ? ((raw ?? 0) / 100).toFixed(2)
      : String(raw ?? '');
    return (
      <TableCell>
        <InlineEditCell
          value={displayValue}
          type={type}
          min={type === 'number' ? 0 : undefined}
          emptyValue={type === 'number' ? 0 : undefined}
          onSave={(v) => {
            // For number cells, v is already normalized by InlineEditCell
            // (never empty/NaN), so plain Number() is safe here.
            let parsed: any;
            if (field === 'price') {
              parsed = Math.round(Number(v) * 100);
            } else {
              parsed = type === 'number' ? Number(v) : v;
            }
            if (isCurrent) setFormValue(field, field === 'price' ? Number(v) : parsed);
            else if (plan) onUpdateOtherPlan(plan.id, { [field]: parsed });
          }}
        />
      </TableCell>
    );
  };

  const settingRows: { key: string; label: string; type: 'text' | 'number' | 'boolean' }[] = [
    { key: 'isActive', label: 'Status', type: 'boolean' },
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'displayName', label: 'Display Name', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'price', label: 'Price ($)', type: 'number' },
    { key: 'currency', label: 'Currency', type: 'text' },
    { key: 'interval', label: 'Billing Interval', type: 'text' },
    { key: 'storageLimitMb', label: 'Storage Limit (MB)', type: 'number' },
    { key: 'aiUpcharge', label: 'AI Upcharge (¢)', type: 'number' },
    { key: 'mediaStorageUpcharge', label: 'Media Storage Upcharge (¢)', type: 'number' },
    { key: 'isFeatured', label: 'Featured', type: 'boolean' },
    { key: 'displayOrder', label: 'Display Order', type: 'number' },
    { key: 'stripePriceId', label: 'Stripe Price ID', type: 'text' },
  ];

  const sectionHeader = (label: string) => (
    <TableRow className="bg-muted/40 hover:bg-muted/40">
      <TableCell colSpan={4} className="font-semibold text-xs uppercase tracking-wide text-muted-foreground py-2">{label}</TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium">Plan Settings — Edit Inline</p>
        <p className="text-xs text-muted-foreground mt-0.5">Click any field to edit. Current plan changes save with "Save Plan". Comparison plan changes save immediately.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-muted-foreground">Compare with:</span>
        <Select value={compareIds[0] || "none"} onValueChange={(v) => setCompareIds([v === 'none' ? '' : v, compareIds[1]])}>
          <SelectTrigger className="w-44" data-testid="select-compare-plan-1">
            <SelectValue placeholder="Select plan…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {available.filter(p => p.id !== compareIds[1]).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={compareIds[1] || "none"} onValueChange={(v) => setCompareIds([compareIds[0], v === 'none' ? '' : v])}>
          <SelectTrigger className="w-44" data-testid="select-compare-plan-2">
            <SelectValue placeholder="Select plan…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {available.filter(p => p.id !== compareIds[0]).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg overflow-auto max-h-[58vh]">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10 shadow-sm">
            <TableRow>
              <TableHead className="w-44 bg-background">Field</TableHead>
              <TableHead className="min-w-[160px] bg-yellow-50/70 dark:bg-yellow-900/10">
                {currentPlan.name} <span className="text-xs font-normal text-muted-foreground ml-1">Current</span>
              </TableHead>
              <TableHead className="min-w-[160px] bg-background">{plan1?.name || <span className="text-muted-foreground text-xs">Not selected</span>}</TableHead>
              <TableHead className="min-w-[160px] bg-background">{plan2?.name || <span className="text-muted-foreground text-xs">Not selected</span>}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sectionHeader('Plan Settings')}
            {settingRows.map(({ key, label, type }) => (
              <TableRow key={key}>
                <TableCell className="text-sm text-muted-foreground font-medium">{label}</TableCell>
                {renderValueCell(null, true, key, type)}
                {renderValueCell(plan1, false, key, type)}
                {renderValueCell(plan2, false, key, type)}
              </TableRow>
            ))}

            {sectionHeader('Menu Access')}
            {FEATURE_REGISTRY.menuItems.map((item) => (
              <TableRow key={`menu-${item}`}>
                <TableCell className="text-sm capitalize">{item.replace(/_/g, ' ')}</TableCell>
                <TableCell className="text-center bg-yellow-50/30 dark:bg-yellow-900/5">
                  <Switch checked={getFeature(null, true, 'menuAccess', item)} onCheckedChange={(v) => saveCurrentFeature('menuAccess', item, v)} />
                </TableCell>
                <TableCell className="text-center">
                  {plan1 ? <Switch checked={getFeature(plan1, false, 'menuAccess', item)} onCheckedChange={(v) => saveOtherFeature(plan1, 'menuAccess', item, v)} /> : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-center">
                  {plan2 ? <Switch checked={getFeature(plan2, false, 'menuAccess', item)} onCheckedChange={(v) => saveOtherFeature(plan2, 'menuAccess', item, v)} /> : <span className="text-muted-foreground">—</span>}
                </TableCell>
              </TableRow>
            ))}

            {sectionHeader('Resource Limits')}
            {FEATURE_REGISTRY.limits.map((limit) => (
              <TableRow key={`limit-${limit}`}>
                <TableCell className="text-sm capitalize">{limit.replace('max_', '').replace(/_/g, ' ')}</TableCell>
                <TableCell className="bg-yellow-50/30 dark:bg-yellow-900/5">
                  <InlineEditCell value={String(getFeature(null, true, 'limits', limit))} type="number" min={0} emptyValue={0} onSave={(v) => saveCurrentFeature('limits', limit, Number(v))} />
                </TableCell>
                <TableCell>
                  {plan1 ? <InlineEditCell value={String(getFeature(plan1, false, 'limits', limit))} type="number" min={0} emptyValue={0} onSave={(v) => saveOtherFeature(plan1, 'limits', limit, Number(v))} /> : <span className="text-muted-foreground text-sm px-2">—</span>}
                </TableCell>
                <TableCell>
                  {plan2 ? <InlineEditCell value={String(getFeature(plan2, false, 'limits', limit))} type="number" min={0} emptyValue={0} onSave={(v) => saveOtherFeature(plan2, 'limits', limit, Number(v))} /> : <span className="text-muted-foreground text-sm px-2">—</span>}
                </TableCell>
              </TableRow>
            ))}

            {sectionHeader('Feature Toggles')}
            {FEATURE_REGISTRY.toggles.map((toggle) => (
              <TableRow key={`toggle-${toggle}`}>
                <TableCell className="text-sm capitalize">{toggle.replace(/_/g, ' ')}</TableCell>
                <TableCell className="text-center bg-yellow-50/30 dark:bg-yellow-900/5">
                  <Switch checked={getFeature(null, true, 'toggles', toggle)} onCheckedChange={(v) => saveCurrentFeature('toggles', toggle, v)} />
                </TableCell>
                <TableCell className="text-center">
                  {plan1 ? <Switch checked={getFeature(plan1, false, 'toggles', toggle)} onCheckedChange={(v) => saveOtherFeature(plan1, 'toggles', toggle, v)} /> : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-center">
                  {plan2 ? <Switch checked={getFeature(plan2, false, 'toggles', toggle)} onCheckedChange={(v) => saveOtherFeature(plan2, 'toggles', toggle, v)} /> : <span className="text-muted-foreground">—</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// Pricing Management Component
export function PricingManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [planDialogTab, setPlanDialogTab] = useState("details");
  const [showPreview, setShowPreview] = useState(false);
  const [showUrlDialog, setShowUrlDialog] = useState(false);
  const [showEmbedDialog, setShowEmbedDialog] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);

  const { data: plans = [], isLoading } = useQuery<SubscriptionPlan[]>({
    queryKey: ["/api/reseller/plans"],
  });

  // AI tier data — used to load/save the matching tier when editing a plan
  const { data: allAiTiers = [] } = useQuery<AiPricingTier[]>({
    queryKey: ["/api/reseller/ai-pricing"],
  });

  const DEFAULT_AI_TIER_CONFIG = {
    tokensIncluded: 0,
    pricePerThousandTokens: '0.0000',
    monthlyMinimum: '0.00',
    maxTokensPerMonth: '' as string | number,
    maxRequestsPerMinute: 30,
    maxRequestsPerDay: 300,
    enableJournalAI: false,
    enableGoalSuggestions: false,
    enableInsights: false,
    enableAdvancedAnalytics: false,
    existingTierId: null as string | null,
  };
  const [aiTierConfig, setAiTierConfig] = useState(DEFAULT_AI_TIER_CONFIG);

  const createPlanMutation = useMutation({
    mutationFn: async (data: any) => {
      return await apiRequest("POST", "/api/reseller/plans", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reseller/plans"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscription-plans"] });
      setIsDialogOpen(false);
      setEditingPlan(null);
      setPlanDialogTab("details");
    },
    onError: (error: any) => {
    },
  });

  const updatePlanMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return await apiRequest("PATCH", `/api/reseller/plans/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reseller/plans"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscription-plans"] });
      setIsDialogOpen(false);
      setEditingPlan(null);
      setPlanDialogTab("details");
    },
    onError: (error: any) => {
    },
  });

  const updatePlanInlineMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return await apiRequest("PATCH", `/api/reseller/plans/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reseller/plans"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscription-plans"] });
    },
    onError: (error: any) => {
    },
  });

  const deletePlanMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest("DELETE", `/api/reseller/plans/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reseller/plans"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscription-plans"] });
    },
    onError: (error: any) => {
    },
  });

  // AI tier create / update — called alongside plan save
  const createAiTierMutation = useMutation({
    mutationFn: async (data: any) => apiRequest("POST", "/api/reseller/ai-pricing", data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/reseller/ai-pricing"] }),
  });
  const updateAiTierMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) =>
      apiRequest("PATCH", `/api/reseller/ai-pricing/${id}`, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/reseller/ai-pricing"] }),
  });

  const syncToStripeMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", "/api/admin/plans/sync-stripe", {});
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/reseller/plans"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscription-plans"] });
      const results = data?.results ?? [];
      const synced = results.filter((r: any) => r.status === "synced").length;
      const skipped = results.filter((r: any) => r.status === "skipped").length;
      const errors = results.filter((r: any) => r.status === "error").length;
      toast({
        title: "Stripe Sync Complete",
        description: `${synced} plan(s) synced, ${skipped} skipped (free/already linked), ${errors} error(s).`,
      });
    },
    onError: () => {
      toast({ title: "Stripe Sync Failed", description: "Could not create Stripe products/prices. Check Stripe configuration.", variant: "destructive" });
    },
  });

  // Replit AI cost (GPT-4o via Replit integration): ~$5/1M blended tokens
  // ~200 requests × 1,500 tokens/req × $5/1M × 1.5 markup = $2.25/month = 225 cents
  const AI_UPCHARGE_DEFAULT = 225;

  // Replit Object Storage: $0.05/GiB/month × 1.5 markup = 7.5 cents/GiB = 7.5/1024 cents/MB
  const calcStorageUpcharge = (mb: number) => Math.max(1, Math.round(mb * 7.5 / 1024));

  // ── Upcharge Calculator state (UI helpers – not stored in DB directly) ──
  const [aiCalc, setAiCalc] = useState({ cost: '', markup: '50' });
  const [storageCalc, setStorageCalc] = useState({ costPerGb: '0.05', markup: '50' });
  const [mediaCalc, setMediaCalc] = useState({ cost: '', markup: '50' });
  const [aiCalcHint, setAiCalcHint] = useState('');
  const [storageCalcHint, setStorageCalcHint] = useState('');
  const [mediaCalcHint, setMediaCalcHint] = useState('');

  const planForm = useForm<any>({
    resolver: zodResolver(insertSubscriptionPlanSchema),
    defaultValues: {
      name: "",
      displayName: "",
      description: "",
      price: 0,
      currency: "USD",
      interval: "month" as const,
      planFeatures: {
        menuAccess: {},
        limits: {},
        toggles: {}
      },
      isActive: true,
      isFeatured: false,
      displayOrder: 0,
      stripePriceId: "",
      landingPagePosition: null,
    },
  });

  useEffect(() => {
    if (editingPlan) {
      planForm.reset({
        name: editingPlan.name,
        displayName: editingPlan.displayName,
        description: editingPlan.description || "",
        price: editingPlan.price / 100,
        currency: editingPlan.currency,
        interval: editingPlan.interval as any,
        planFeatures: (editingPlan.planFeatures as any) || {
          menuAccess: {},
          limits: {},
          toggles: {}
        },
        storageLimitMb: editingPlan.storageLimitMb ?? 100,
        aiUpcharge: editingPlan.aiUpcharge ?? 0,
        mediaStorageUpcharge: editingPlan.mediaStorageUpcharge ?? 0,
        isActive: editingPlan.isActive,
        isFeatured: editingPlan.isFeatured,
        displayOrder: editingPlan.displayOrder,
        stripePriceId: editingPlan.stripePriceId || "",
        landingPagePosition: editingPlan.landingPagePosition as any,
      });
      // Pre-populate calculator from stored values (reverse 50% default markup)
      const aiMonthly = (editingPlan.aiUpcharge ?? 0) / 100;
      setAiCalc({ cost: aiMonthly > 0 ? (aiMonthly / 1.5).toFixed(2) : '', markup: '50' });
      const mediaMonthly = (editingPlan.mediaStorageUpcharge ?? 0) / 100;
      setMediaCalc({ cost: mediaMonthly > 0 ? (mediaMonthly / 1.5).toFixed(2) : '', markup: '50' });
    } else {
      planForm.reset({
        name: "",
        displayName: "",
        description: "",
        price: 0,
        currency: "USD",
        interval: "month" as const,
        planFeatures: {
          menuAccess: {},
          limits: {},
          toggles: {}
        },
        storageLimitMb: 100,
        aiUpcharge: AI_UPCHARGE_DEFAULT,
        mediaStorageUpcharge: calcStorageUpcharge(100),
        isActive: true,
        isFeatured: false,
        displayOrder: plans.length,
        stripePriceId: "",
        landingPagePosition: null,
      });
      // Default calculator state for new plans
      setAiCalc({ cost: (AI_UPCHARGE_DEFAULT / 100 / 1.5).toFixed(2), markup: '50' });
      setMediaCalc({ cost: (calcStorageUpcharge(100) / 100 / 1.5).toFixed(2), markup: '50' });
    }
  }, [editingPlan, plans.length, planForm]);

  // Initialise AI tier config whenever the editing plan or the tiers data change
  useEffect(() => {
    if (editingPlan) {
      const match = (allAiTiers as AiPricingTier[]).find(t => t.name === editingPlan.name);
      if (match) {
        setAiTierConfig({
          tokensIncluded: match.tokensIncluded,
          pricePerThousandTokens: String(match.pricePerThousandTokens),
          monthlyMinimum: String(match.monthlyMinimum),
          maxTokensPerMonth: match.maxTokensPerMonth ?? '',
          maxRequestsPerMinute: match.maxRequestsPerMinute,
          maxRequestsPerDay: match.maxRequestsPerDay,
          enableJournalAI: match.enableJournalAI,
          enableGoalSuggestions: match.enableGoalSuggestions,
          enableInsights: match.enableInsights,
          enableAdvancedAnalytics: match.enableAdvancedAnalytics,
          existingTierId: match.id,
        });
      } else {
        setAiTierConfig(DEFAULT_AI_TIER_CONFIG);
      }
    } else {
      setAiTierConfig(DEFAULT_AI_TIER_CONFIG);
    }
  }, [editingPlan, allAiTiers]);

  // Auto-update mediaStorageUpcharge when storageLimitMb changes on NEW plans
  const watchedStorageMb = planForm.watch("storageLimitMb");
  useEffect(() => {
    if (!editingPlan) {
      planForm.setValue("mediaStorageUpcharge", calcStorageUpcharge(watchedStorageMb || 100), { shouldDirty: false });
    }
  }, [watchedStorageMb, editingPlan]);

  // ── Derived calculator values ──
  const storageGb = (watchedStorageMb || 0) / 1024;
  const storageCostPerGbNum = parseFloat(storageCalc.costPerGb);
  const storageMarkupNum = parseFloat(storageCalc.markup);
  const storageInputsValid =
    storageCalc.costPerGb !== '' && !isNaN(storageCostPerGbNum) &&
    storageCalc.markup !== '' && !isNaN(storageMarkupNum);
  const storagePlatformMonthlyCost = storageInputsValid ? storageGb * storageCostPerGbNum : null;
  const storageCustomerRatePerGb = storageInputsValid
    ? storageCostPerGbNum * (1 + storageMarkupNum / 100)
    : null;

  // Derived display values — only computed when both fields hold valid numbers
  const aiCostNum = parseFloat(aiCalc.cost);
  const aiMarkupNum = parseFloat(aiCalc.markup);
  const aiInputsValid = aiCalc.cost !== '' && !isNaN(aiCostNum) && aiCalc.markup !== '' && !isNaN(aiMarkupNum);
  const aiCustomerPrice = aiInputsValid ? aiCostNum * (1 + aiMarkupNum / 100) : null;

  const mediaCostNum = parseFloat(mediaCalc.cost);
  const mediaMarkupNum = parseFloat(mediaCalc.markup);
  const mediaInputsValid = mediaCalc.cost !== '' && !isNaN(mediaCostNum) && mediaCalc.markup !== '' && !isNaN(mediaMarkupNum);
  const mediaCustomerPrice = mediaInputsValid ? mediaCostNum * (1 + mediaMarkupNum / 100) : null;

  // Update handlers: update string state only — form is synced on blur/Enter (commit)
  const updateAiCalc = (updates: Partial<{ cost: string; markup: string }>) => {
    setAiCalc(prev => ({ ...prev, ...updates }));
    setAiCalcHint('');
  };

  const commitAiCalc = () => {
    const result = calcAiCommit(aiCalc.cost, aiCalc.markup);
    setAiCalcHint(result.hint);
    if (result.upchargeInCents !== undefined) {
      planForm.setValue('aiUpcharge', result.upchargeInCents, { shouldDirty: true });
    }
  };

  const updateMediaCalc = (updates: Partial<{ cost: string; markup: string }>) => {
    setMediaCalc(prev => ({ ...prev, ...updates }));
    setMediaCalcHint('');
  };

  const commitMediaCalc = () => {
    const result = calcMediaCommit(mediaCalc.cost, mediaCalc.markup);
    setMediaCalcHint(result.hint);
    if (result.upchargeInCents !== undefined) {
      planForm.setValue('mediaStorageUpcharge', result.upchargeInCents, { shouldDirty: true });
    }
  };

  const commitStorageCalc = () => {
    const result = calcStorageCommit(storageCalc.costPerGb, storageCalc.markup);
    setStorageCalcHint(result.hint);
  };

  const onSubmitPlan = (data: any) => {
    const planName = (data.name || '').toLowerCase().replace(/[^a-z0-9_-]/g, '') || data.name;
    // price is a number from NumberField (empty commits 0 via emptyValue)
    const submitData = { ...data, price: Math.round(Number(data.price ?? 0) * 100) };

    if (editingPlan) {
      updatePlanMutation.mutate({ id: editingPlan.id, data: submitData });
    } else {
      createPlanMutation.mutate(submitData);
    }

    // Also create/update the linked AI tier
    const { existingTierId, ...tierFields } = aiTierConfig;
    const tierData = {
      name: planName,
      displayName: data.displayName || planName,
      description: `AI configuration for ${data.displayName || planName} plan`,
      ...tierFields,
      maxTokensPerMonth: tierFields.maxTokensPerMonth !== '' ? parseInt(String(tierFields.maxTokensPerMonth)) : null,
      isActive: data.isActive ?? true,
      isRecommended: data.isFeatured ?? false,
      priority: data.displayOrder ?? 0,
    };
    if (existingTierId) {
      updateAiTierMutation.mutate({ id: existingTierId, data: tierData });
    } else {
      createAiTierMutation.mutate(tierData);
    }
  };

  const sortedPlans = [...plans].sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Pricing Plans Management
          </h3>
          <p className="text-sm text-muted-foreground">
            Create and manage subscription plans with live pricing preview
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Preview button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPreview(true)}
            title="Preview the public pricing page"
          >
            <Eye className="w-4 h-4 mr-1.5" />
            Preview
          </Button>

          {/* URL button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowUrlDialog(true)}
            title="Get shareable public pricing URL"
          >
            <Link2 className="w-4 h-4 mr-1.5" />
            URL
          </Button>

          {/* Embed button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowEmbedDialog(true)}
            title="Get embed code for your website"
          >
            <Code2 className="w-4 h-4 mr-1.5" />
            Embed
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => syncToStripeMutation.mutate()}
            disabled={syncToStripeMutation.isPending}
            title="Create Stripe products & prices for all paid plans that are not yet linked"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${syncToStripeMutation.isPending ? "animate-spin" : ""}`} />
            Sync to Stripe
          </Button>

          {/* Preview Dialog */}
          <Dialog open={showPreview} onOpenChange={setShowPreview}>
            <DialogContent className="w-screen h-screen m-0 rounded-none p-0 flex flex-col">
              <DialogHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
                <div>
                  <DialogTitle className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Pricing Page Preview
                  </DialogTitle>
                  <DialogDescription>
                    This is how your public pricing page looks to visitors.
                  </DialogDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open("/publicpricing", "_blank")}
                >
                  <ExternalLink className="w-4 h-4 mr-1.5" />
                  Open in Tab
                </Button>
              </DialogHeader>
              <iframe
                src="/publicpricing"
                className="flex-1 w-full border-0"
                title="Pricing page preview"
              />
            </DialogContent>
          </Dialog>

          {/* URL Dialog */}
          <Dialog open={showUrlDialog} onOpenChange={setShowUrlDialog}>
            <DialogContent >
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Public Pricing URL
                </DialogTitle>
                <DialogDescription>
                  Share this link in emails, social media, or anywhere to let people sign up directly.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-muted rounded-md px-3 py-2 text-sm font-mono break-all select-all">
                    {window.location.origin}/publicpricing
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/publicpricing`);
                      setUrlCopied(true);
                      setTimeout(() => setUrlCopied(false), 2000);
                    }}
                  >
                    {urlCopied ? <CheckCheck className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  This page is publicly accessible — no login required to view pricing. Users who click a plan will be prompted to sign in or create an account.
                </p>
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => window.open("/publicpricing", "_blank")}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Page
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Embed Dialog */}
          <Dialog open={showEmbedDialog} onOpenChange={setShowEmbedDialog}>
            <DialogContent >
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Code2 className="w-4 h-4" />
                  Embed Pricing Page
                </DialogTitle>
                <DialogDescription>
                  Copy this code and paste it into your website or app to embed the pricing page inline.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="relative">
                  <pre className="bg-muted rounded-md px-4 py-3 text-sm font-mono whitespace-pre-wrap break-all overflow-auto max-h-48">{`<iframe
  src="${window.location.origin}/publicpricing"
  width="100%"
  height="800"
  style="border: none; border-radius: 8px;"
  title="Pricing Plans"
  loading="lazy"
></iframe>`}</pre>
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `<iframe\n  src="${window.location.origin}/publicpricing"\n  width="100%"\n  height="800"\n  style="border: none; border-radius: 8px;"\n  title="Pricing Plans"\n  loading="lazy"\n></iframe>`
                      );
                      setEmbedCopied(true);
                      setTimeout(() => setEmbedCopied(false), 2000);
                    }}
                  >
                    {embedCopied ? <CheckCheck className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Paste this snippet anywhere in your HTML. Adjust the <code className="text-xs bg-muted px-1 rounded">height</code> value to fit your layout. The page is fully responsive.
                </p>
              </div>
            </DialogContent>
          </Dialog>

        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) setPlanDialogTab("details"); }}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => { setEditingPlan(null); setPlanDialogTab("details"); }}
              data-testid="button-add-pricing-plan"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Plan
            </Button>
          </DialogTrigger>
          <DialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPlan ? "Edit Subscription Plan" : "Create Subscription Plan"}
              </DialogTitle>
              <DialogDescription>
                Configure a new pricing plan for your customers. This will appear on your pricing page.
              </DialogDescription>
            </DialogHeader>
            <Tabs value={planDialogTab} onValueChange={setPlanDialogTab} className="mt-2">
              <TabsList>
                <TabsTrigger value="details">Plan Details</TabsTrigger>
                <TabsTrigger value="ai-usage" className="flex items-center gap-1">
                  <Bot className="w-3.5 h-3.5" />
                  AI &amp; Usage Costs
                </TabsTrigger>
                <TabsTrigger value="features">
                  Feature Toggles ({FEATURE_REGISTRY.menuItems.length + FEATURE_REGISTRY.limits.length + FEATURE_REGISTRY.toggles.length})
                </TabsTrigger>
                <TabsTrigger value="compare" disabled={!editingPlan}>Compare Plans</TabsTrigger>
              </TabsList>
              <Form {...planForm}>
                <form onSubmit={planForm.handleSubmit(onSubmitPlan)} className="space-y-4 mt-4">
                  <TabsContent value="details" className="space-y-4 mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={planForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Plan Name (Internal)</FormLabel>
                        <FormControl>
                          <Input placeholder="basic-plan" {...field} data-testid="input-plan-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={planForm.control}
                    name="displayName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Display Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Basic Plan" {...field} data-testid="input-plan-display-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={planForm.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Perfect for individuals getting started..."
                          {...field} 
                          data-testid="textarea-plan-description"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={planForm.control}
                    name="price"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Price ($)</FormLabel>
                        <FormControl>
                          <NumberField
                            step="0.01"
                            min={0}
                            allowDecimal
                            emptyValue={0}
                            placeholder="9.99"
                            name={field.name}
                            onBlur={field.onBlur}
                            ref={field.ref}
                            value={field.value}
                            onValueChange={(v) => field.onChange(v ?? 0)}
                            data-testid="input-plan-price"
                          />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">Enter the price in dollars (e.g. 9.99 for $9.99)</p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={planForm.control}
                    name="currency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Currency</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-plan-currency">
                              <SelectValue placeholder="Currency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="GBP">GBP</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={planForm.control}
                    name="interval"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Billing</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-plan-billing">
                              <SelectValue placeholder="Interval" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="month">Monthly</SelectItem>
                            <SelectItem value="year">Yearly</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={planForm.control}
                  name="stripePriceId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Stripe Price ID (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="price_1ABC..." {...field} data-testid="input-plan-stripe-id" />
                      </FormControl>
                      <FormDescription>
                        Link this plan to a Stripe price for payment processing
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={planForm.control}
                  name="landingPagePosition"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Landing Page Position</FormLabel>
                      <Select
                        value={field.value ? String(field.value) : "none"}
                        onValueChange={(value) => field.onChange(value === "none" ? null : parseInt(value))}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-landing-position">
                            <SelectValue placeholder="Select position (optional)" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">Not shown on landing page</SelectItem>
                          <SelectItem value="1">Plan 1 (Left)</SelectItem>
                          <SelectItem value="2">Plan 2 (Middle)</SelectItem>
                          <SelectItem value="3">Plan 3 (Right)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Choose which position this plan shows on the landing page (1=left, 2=middle, 3=right)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Hidden bindings kept so form tracks these values even when AI & Usage tab isn't mounted */}
                <FormField control={planForm.control} name="storageLimitMb" render={({ field }) => (
                  <input type="hidden" {...field} />
                )} />
                <FormField control={planForm.control} name="aiUpcharge" render={({ field }) => (
                  <input type="hidden" {...field} data-testid="input-ai-upcharge" />
                )} />
                <FormField control={planForm.control} name="mediaStorageUpcharge" render={({ field }) => (
                  <input type="hidden" {...field} data-testid="input-media-storage-upcharge" />
                )} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={planForm.control}
                    name="isActive"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} data-testid="checkbox-plan-active" />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Active Plan</FormLabel>
                          <FormDescription>Make this plan available to customers</FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={planForm.control}
                    name="isFeatured"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} data-testid="checkbox-plan-featured" />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Featured Plan</FormLabel>
                          <FormDescription>Highlight this plan as recommended</FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
                  </TabsContent>

                  {/* ══ AI & Usage Costs tab ══ */}
                  <TabsContent value="ai-usage" className="space-y-6 mt-0">

                    {/* ── Section 1: Upcharge Calculator ── */}
                    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Calculator className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="text-sm font-semibold">Upcharge Calculator</span>
                        <span className="text-xs text-muted-foreground">Enter your platform costs + markup to auto-set customer upcharges</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                        {/* AI Usage card */}
                        <div className="rounded-md border bg-background p-3 space-y-3">
                          <div className="flex items-center gap-1.5 text-sm font-medium">
                            <Cpu className="w-3.5 h-3.5 text-violet-500" />AI Usage
                          </div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Info className="w-3 h-3 shrink-0" />
                            <span>~$5/1M tokens (Replit AI) · $0.18/1K (GPT-4o)</span>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <label className="text-xs text-muted-foreground">Your platform AI cost / month ($)</label>
                              <Input type="number" step="0.01" min="0" placeholder="1.50" className="mt-1 h-8 text-sm"
                                value={aiCalc.cost}
                                onChange={(e) => updateAiCalc({ cost: e.target.value })}
                                onBlur={commitAiCalc}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitAiCalc(); } }}
                                data-testid="input-ai-platform-cost" />
                              <p className="text-xs text-muted-foreground mt-0.5">e.g. 300K tokens × $5/1M = $1.50</p>
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground">Markup (%)</label>
                              <div className="flex items-center gap-1 mt-1">
                                <TrendingUp className="w-3 h-3 text-muted-foreground shrink-0" />
                                <Input type="number" min="0" placeholder="50" className="h-8 text-sm"
                                  value={aiCalc.markup}
                                  onChange={(e) => updateAiCalc({ markup: e.target.value })}
                                  onBlur={commitAiCalc}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitAiCalc(); } }}
                                  data-testid="input-ai-markup" />
                                <span className="text-xs text-muted-foreground">%</span>
                              </div>
                            </div>
                          </div>
                          <div className="rounded bg-violet-50 dark:bg-violet-900/20 border border-violet-100 dark:border-violet-800 p-2 space-y-0.5">
                            <div className="text-xs text-muted-foreground">Customer AI upcharge / month</div>
                            {aiCustomerPrice !== null ? (
                              <>
                                <div className="text-xl font-bold text-violet-700 dark:text-violet-300">${aiCustomerPrice.toFixed(2)}</div>
                                <div className="text-xs text-muted-foreground">Stored as {Math.round(aiCustomerPrice * 100)}¢</div>
                              </>
                            ) : (
                              <div className="text-sm font-medium text-violet-400 dark:text-violet-500">—</div>
                            )}
                            {aiCalcHint && <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">{aiCalcHint}</p>}
                          </div>
                        </div>

                        {/* File Storage card */}
                        <div className="rounded-md border bg-background p-3 space-y-3">
                          <div className="flex items-center gap-1.5 text-sm font-medium">
                            <HardDrive className="w-3.5 h-3.5 text-blue-500" />File Storage
                          </div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Info className="w-3 h-3 shrink-0" />
                            <span>~$0.05/GB/mo (Replit Object Storage)</span>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <label className="text-xs text-muted-foreground">Included storage limit (MB)</label>
                              <NumberField placeholder="100" className="mt-1 h-8 text-sm"
                                min={0}
                                emptyValue={100}
                                value={planForm.watch('storageLimitMb')}
                                onValueChange={(v) => planForm.setValue('storageLimitMb', v ?? 100, { shouldDirty: true })}
                                data-testid="input-storage-limit" />
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground">Your cost ($/GB/month)</label>
                              <Input type="number" step="0.001" min="0" placeholder="0.05" className="mt-1 h-8 text-sm"
                                value={storageCalc.costPerGb}
                                onChange={(e) => { setStorageCalc(s => ({ ...s, costPerGb: e.target.value })); setStorageCalcHint(''); }}
                                onBlur={commitStorageCalc}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitStorageCalc(); } }}
                                data-testid="input-storage-cost-per-gb" />
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground">Markup (%)</label>
                              <div className="flex items-center gap-1 mt-1">
                                <TrendingUp className="w-3 h-3 text-muted-foreground shrink-0" />
                                <Input type="number" min="0" placeholder="50" className="h-8 text-sm"
                                  value={storageCalc.markup}
                                  onChange={(e) => { setStorageCalc(s => ({ ...s, markup: e.target.value })); setStorageCalcHint(''); }}
                                  onBlur={commitStorageCalc}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitStorageCalc(); } }}
                                  data-testid="input-storage-markup" />
                                <span className="text-xs text-muted-foreground">%</span>
                              </div>
                            </div>
                          </div>
                          <div className="rounded bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 p-2 space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-muted-foreground">Your monthly cost</span>
                              <span className="font-medium text-blue-700 dark:text-blue-300">
                                {storagePlatformMonthlyCost !== null ? `$${storagePlatformMonthlyCost.toFixed(4)}/mo` : '—'}
                              </span>
                            </div>
                            <div className="border-t border-blue-100 dark:border-blue-800 pt-1">
                              <div className="text-xs text-muted-foreground">Customer overage rate</div>
                              <div className="text-xl font-bold text-blue-700 dark:text-blue-300">
                                {storageCustomerRatePerGb !== null ? `$${storageCustomerRatePerGb.toFixed(4)}/GB/mo` : '—'}
                              </div>
                            </div>
                            {storageCalcHint && <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">{storageCalcHint}</p>}
                          </div>
                        </div>

                        {/* Media Storage card */}
                        <div className="rounded-md border bg-background p-3 space-y-3">
                          <div className="flex items-center gap-1.5 text-sm font-medium">
                            <Film className="w-3.5 h-3.5 text-rose-500" />Media Storage
                          </div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Info className="w-3 h-3 shrink-0" />
                            <span>~$0.05/GB/mo + CDN/bandwidth overhead</span>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <label className="text-xs text-muted-foreground">Your platform media cost / month ($)</label>
                              <Input type="number" step="0.01" min="0" placeholder="0.50" className="mt-1 h-8 text-sm"
                                value={mediaCalc.cost}
                                onChange={(e) => updateMediaCalc({ cost: e.target.value })}
                                onBlur={commitMediaCalc}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitMediaCalc(); } }}
                                data-testid="input-media-platform-cost" />
                              <p className="text-xs text-muted-foreground mt-0.5">e.g. 10 GB × $0.05/GB = $0.50</p>
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground">Markup (%)</label>
                              <div className="flex items-center gap-1 mt-1">
                                <TrendingUp className="w-3 h-3 text-muted-foreground shrink-0" />
                                <Input type="number" min="0" placeholder="50" className="h-8 text-sm"
                                  value={mediaCalc.markup}
                                  onChange={(e) => updateMediaCalc({ markup: e.target.value })}
                                  onBlur={commitMediaCalc}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitMediaCalc(); } }}
                                  data-testid="input-media-markup" />
                                <span className="text-xs text-muted-foreground">%</span>
                              </div>
                            </div>
                          </div>
                          <div className="rounded bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800 p-2 space-y-0.5">
                            <div className="text-xs text-muted-foreground">Customer media upcharge / month</div>
                            {mediaCustomerPrice !== null ? (
                              <>
                                <div className="text-xl font-bold text-rose-700 dark:text-rose-300">${mediaCustomerPrice.toFixed(2)}</div>
                                <div className="text-xs text-muted-foreground">Stored as {Math.round(mediaCustomerPrice * 100)}¢</div>
                              </>
                            ) : (
                              <div className="text-sm font-medium text-rose-400 dark:text-rose-500">—</div>
                            )}
                            {mediaCalcHint && <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">{mediaCalcHint}</p>}
                          </div>
                        </div>

                      </div>
                    </div>

                    {/* ── Section 2: AI Token Configuration (merged from AI Pricing Tiers) ── */}
                    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-4">
                      <div className="flex items-center gap-2">
                        <Bot className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="text-sm font-semibold">AI Token Limits &amp; Features</span>
                        <span className="text-xs text-muted-foreground">Controls actual AI usage enforcement for customers on this plan</span>
                      </div>

                      {/* Pricing row */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Monthly minimum ($)</label>
                          <Input type="number" step="0.01" min="0" placeholder="0.00" className="h-8 text-sm"
                            value={aiTierConfig.monthlyMinimum}
                            onChange={(e) => setAiTierConfig(s => ({ ...s, monthlyMinimum: e.target.value }))} />
                          <p className="text-xs text-muted-foreground">Minimum charge even if usage is zero</p>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Tokens included / month</label>
                          <NumberField min={0} emptyValue={0} placeholder="10000" className="h-8 text-sm"
                            data-testid="input-ai-tier-tokens-included"
                            value={aiTierConfig.tokensIncluded}
                            onValueChange={(v) => setAiTierConfig(s => ({ ...s, tokensIncluded: v ?? 0 }))} />
                          <p className="text-xs text-muted-foreground">Free token allowance before billing starts</p>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Price per 1K tokens ($)</label>
                          <Input type="number" step="0.0001" min="0" placeholder="0.0200" className="h-8 text-sm"
                            value={aiTierConfig.pricePerThousandTokens}
                            onChange={(e) => setAiTierConfig(s => ({ ...s, pricePerThousandTokens: e.target.value }))} />
                          <p className="text-xs text-muted-foreground">Overage rate after free tokens used</p>
                        </div>
                      </div>

                      {/* Rate limits */}
                      <div className="rounded-md border bg-background p-3 space-y-3">
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Rate Limits</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-1">
                            <label className="text-xs text-muted-foreground">Max requests / minute</label>
                            <NumberField min={1} emptyValue={30} placeholder="30" className="h-8 text-sm"
                              data-testid="input-ai-tier-max-requests-minute"
                              value={aiTierConfig.maxRequestsPerMinute}
                              onValueChange={(v) => setAiTierConfig(s => ({ ...s, maxRequestsPerMinute: v ?? 30 }))} />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-muted-foreground">Max requests / day</label>
                            <NumberField min={1} emptyValue={300} placeholder="300" className="h-8 text-sm"
                              data-testid="input-ai-tier-max-requests-day"
                              value={aiTierConfig.maxRequestsPerDay}
                              onValueChange={(v) => setAiTierConfig(s => ({ ...s, maxRequestsPerDay: v ?? 300 }))} />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-muted-foreground">Max tokens / month (blank = unlimited)</label>
                            <Input type="number" min="0" placeholder="Unlimited" className="h-8 text-sm"
                              value={aiTierConfig.maxTokensPerMonth}
                              onChange={(e) => setAiTierConfig(s => ({ ...s, maxTokensPerMonth: e.target.value }))} />
                          </div>
                        </div>
                      </div>

                      {/* AI feature flags */}
                      <div className="rounded-md border bg-background p-3 space-y-3">
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">AI Features enabled for this plan</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {([
                            { key: 'enableJournalAI', label: 'AI Journal Insights' },
                            { key: 'enableGoalSuggestions', label: 'Smart Goal Suggestions' },
                            { key: 'enableInsights', label: 'Productivity Insights' },
                            { key: 'enableAdvancedAnalytics', label: 'Advanced Analytics' },
                          ] as const).map(({ key, label }) => (
                            <div key={key} className="flex items-center space-x-2">
                              <Checkbox
                                id={`aitier-${key}`}
                                checked={aiTierConfig[key] as boolean}
                                onCheckedChange={(v) => setAiTierConfig(s => ({ ...s, [key]: v as boolean }))}
                                data-testid={`checkbox-${key}`}
                              />
                              <label htmlFor={`aitier-${key}`} className="text-sm cursor-pointer">{label}</label>
                            </div>
                          ))}
                        </div>
                      </div>

                      {aiTierConfig.existingTierId && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Check className="w-3 h-3 text-green-500" />
                          Linked to AI tier — changes save automatically when you save the plan.
                        </p>
                      )}
                      {!aiTierConfig.existingTierId && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Info className="w-3 h-3" />
                          A new AI tier will be created and linked to this plan when you save.
                        </p>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="features" className="mt-0">
                    <FormField
                      control={planForm.control}
                      name="planFeatures"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Plan Features & Limits</FormLabel>
                          <FormControl>
                            <StructuredPlanFeaturesEditor
                              key={editingPlan?.id || 'new-plan'}
                              planFeatures={field.value || { menuAccess: {}, limits: {}, toggles: {} }}
                              onChange={field.onChange}
                            />
                          </FormControl>
                          <FormDescription>
                            Configure menu access, resource limits, and feature toggles for this plan
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </TabsContent>

                  <TabsContent value="compare" className="mt-0">
                    {editingPlan ? (
                      <ComparePlansTab
                        currentPlan={editingPlan}
                        plans={plans}
                        getFormValue={(field) => planForm.getValues(field as any)}
                        setFormValue={(field, value) => planForm.setValue(field as any, value)}
                        onUpdateOtherPlan={(planId, data) => updatePlanInlineMutation.mutate({ id: planId, data })}
                      />
                    ) : (
                      <p className="text-muted-foreground text-sm py-4">Save the plan first to use the comparison view.</p>
                    )}
                  </TabsContent>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); setPlanDialogTab("details"); }} data-testid="button-cancel-plan">
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={createPlanMutation.isPending || updatePlanMutation.isPending}
                      data-testid="button-save-plan"
                    >
                      {(createPlanMutation.isPending || updatePlanMutation.isPending) ? "Saving..." : "Save Plan"}
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            </Tabs>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Visual Plans Editor */}
      <Card>
        <CardHeader>
          <CardTitle>Visual Pricing Plans Editor</CardTitle>
          <CardDescription>
            View and manage your pricing plans with inline feature editing. Click "Edit Details" to modify plan settings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No pricing plans configured</p>
              <p className="text-sm">Click "Add Plan" to get started</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {sortedPlans.map((plan) => (
                <VisualPlanCard 
                  key={plan.id} 
                  plan={plan} 
                  onEdit={(plan) => {
                    setEditingPlan(plan);
                    setIsDialogOpen(true);
                    setPlanDialogTab("details");
                  }}
                  onDelete={() => deletePlanMutation.mutate(plan.id)}
                  onUpdate={(updatedPlan) => updatePlanMutation.mutate({ id: plan.id, data: updatedPlan })}
                  isDeleting={deletePlanMutation.isPending}
                  isUpdating={updatePlanMutation.isPending}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
